import React, { useState, useRef } from 'react';
import { X, CheckCircle, Save, Package, Droplet, Droplets, Shirt, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const ModalCompletarEntrega = ({ entrega, isOpen, onClose, onConfirm }) => {
  const [receptorNombre, setReceptorNombre] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [itemsVerificados, setItemsVerificados] = useState({});
  const [cantidadesEntregadas, setCantidadesEntregadas] = useState({}); // Nuevo estado para cantidades
  const [pesoReal, setPesoReal] = useState(''); // Peso real total (kg) confirmado por el usuario
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const signatureRef = useRef(null);

  // Inicializar items verificados, cantidades y peso real sugerido
  React.useEffect(() => {
    if (entrega?.items) {
      const verified = {};
      const cantidades = {};
      let pesoEstimadoG = 0;
      entrega.items.forEach(item => {
        verified[item.id] = true; // Por defecto todos verificados
        cantidades[item.id] = item.cantidad; // Cantidad original
        pesoEstimadoG += (item.peso || 0) * (item.cantidad || 0);
      });
      setItemsVerificados(verified);
      setCantidadesEntregadas(cantidades);
      // Sugerir el peso estimado (en kg); el usuario puede confirmar o cambiar
      setPesoReal((pesoEstimadoG / 1000).toFixed(2));
    }
  }, [entrega]);

  // Early return DESPUÉS de todos los hooks
  if (!isOpen || !entrega) return null;

  const TipoIcon = entrega.tipo === 'lenceria' ? Package : Droplet;
  const tipoLabel = entrega.tipo === 'lenceria' ? 'Lencería' : 'Insumos';

  // El peso real total aplica a la lencería vertical (prendas con peso)
  const requierePesoReal = entrega.tipo === 'lenceria' && entrega.logistica === 'vertical';

  const handleToggleItem = (itemId) => {
    setItemsVerificados(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
    limpiarError('items');
  };

  const handleIncrementCantidad = (itemId) => {
    setCantidadesEntregadas(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1
    }));
  };

  const handleDecrementCantidad = (itemId) => {
    setCantidadesEntregadas(prev => ({
      ...prev,
      [itemId]: Math.max((prev[itemId] || 0) - 1, 0)
    }));
  };

  const handleCantidadChange = (itemId, valor) => {
    const cantidad = parseInt(valor) || 0;
    setCantidadesEntregadas(prev => ({
      ...prev,
      [itemId]: Math.max(cantidad, 0)
    }));
  };

  const handleClearSignature = () => {
    signatureRef.current?.clear();
  };

  // Limpia el error de un campo puntual (al corregirlo)
  const limpiarError = (campo) => {
    setErrors(prev => {
      if (!prev[campo]) return prev;
      const next = { ...prev };
      delete next[campo];
      return next;
    });
  };

  // Valida los campos obligatorios y devuelve el objeto de errores
  const validate = () => {
    const nuevosErrores = {};

    if (!receptorNombre.trim()) {
      nuevosErrores.receptorNombre = 'Ingrese el nombre de quien recibe';
    }
    if (signatureRef.current?.isEmpty()) {
      nuevosErrores.firma = 'La firma del receptor es obligatoria';
    }
    const algunoVerificado = Object.values(itemsVerificados).some(v => v);
    if (!algunoVerificado) {
      nuevosErrores.items = 'Debe verificar al menos un item';
    }
    // El peso real solo aplica a lencería vertical (las prendas tienen peso)
    if (requierePesoReal) {
      const pesoRealNum = parseFloat(pesoReal);
      if (!pesoReal || isNaN(pesoRealNum) || pesoRealNum <= 0) {
        nuevosErrores.pesoReal = 'Confirme el peso real total (mayor a 0)';
      }
    }

    setErrors(nuevosErrores);
    return nuevosErrores;
  };

  // Items entregados con cantidades reales (solo verificados y con cantidad > 0)
  const construirItemsEntregados = () =>
    entrega.items
      .filter(item => itemsVerificados[item.id])
      .map(item => ({
        ...item,
        cantidad: cantidadesEntregadas[item.id] || item.cantidad
      }))
      .filter(item => item.cantidad > 0);

  // Peso estimado (kg) según las prendas verificadas y sus cantidades a entregar
  const calcularPesoEstimadoKg = () =>
    (construirItemsEntregados().reduce(
      (sum, item) => sum + ((item.peso || 0) * (item.cantidad || 0)),
      0
    ) / 1000).toFixed(2);

  // Coloca el peso estimado como peso real (sugerencia)
  const usarPesoSugerido = () => {
    setPesoReal(calcularPesoEstimadoKg());
    limpiarError('pesoReal');
  };

  // Prepara los datos para imprimir el ticket de entrega
  const buildDatosImpresion = (fechaEntrega) => ({
    ...entrega,
    consecutivo: entrega.consecutivo || entrega.solicitud_id,
    fechaEntrega,
    receptor_nombre: receptorNombre,
    observaciones,
    items: construirItemsEntregados(),
    // Peso real confirmado (gramos) para que salga en el ticket
    peso_real: requierePesoReal ? Math.round(parseFloat(pesoReal) * 1000) : entrega.peso_real,
    entregadoPor: { nombre: entrega.conductor?.nombre || 'Sistema' }
  });

  const handleSubmit = async () => {
    const nuevosErrores = validate();
    if (Object.keys(nuevosErrores).length > 0) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setIsSubmitting(true);
    try {
      const fechaEntrega = new Date().toISOString();
      const itemsActualizados = construirItemsEntregados();

      const datosEntrega = {
        entrega_id: entrega._id,
        tipo: entrega.tipo,
        logistica: entrega.logistica,
        receptor_nombre: receptorNombre,
        observaciones,
        firma: signatureRef.current.toDataURL(),
        items: itemsActualizados, // ✅ Items modificados con cantidades reales
        // Peso real total (gramos) - solo aplica a lencería vertical
        peso_real: requierePesoReal
          ? Math.round(parseFloat(pesoReal) * 1000)
          : undefined,
        fecha_entrega: fechaEntrega,
        ubicacion: null,
      };

      // Conectar la impresora Bluetooth dentro del gesto del usuario
      // (requestDevice requiere activación del usuario).
      const printer = getPrinterInstance();
      let impresoraLista = printer.isConnected();
      if (!impresoraLista) {
        try {
          await printer.connect();
          impresoraLista = true;
        } catch (connError) {
          console.error('No se pudo conectar la impresora Bluetooth:', connError);
        }
      }

      await onConfirm(datosEntrega);

      // Imprimir solo si la impresora quedó conectada. Si no, avisamos y NO
      // reintentamos (evita un segundo diálogo que deja el modal colgado).
      if (impresoraLista) {
        try {
          await printer.printEntregaDobleCopia(buildDatosImpresion(fechaEntrega));
          toast.success('Entrega completada exitosamente');
        } catch (printError) {
          console.error('Error al imprimir el ticket de entrega:', printError);
          toast.error('Entrega completada, pero no se pudo imprimir el ticket');
        }
      } else {
        toast.info('Entrega completada. No se pudo conectar la impresora; puedes reimprimir el comprobante luego.');
      }

      onClose();
    } catch (error) {
      console.error('Error al completar entrega:', error);
      toast.error('Error al completar la entrega');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <TipoIcon className={`h-5 w-5 ${
              entrega.tipo === 'lenceria' ? 'text-blue-600' : 'text-purple-600'
            }`} />
            <h2 className="text-lg font-semibold">Completar Entrega - {tipoLabel}</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Información de la entrega */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">
                {entrega.edificio?.nombre}
              </span>
              <Badge className="bg-yellow-100 text-yellow-700">
                En Camino
              </Badge>
            </div>
            {(entrega.hospedaje?.nombre || entrega.hospedaje?.nombrePropiedad) && (
              <p className="text-xs text-gray-600">
                <span className="font-medium">Activo:</span> {entrega.hospedaje?.nombre || entrega.hospedaje?.nombrePropiedad}
              </p>
            )}
            <p className="text-xs text-gray-600">
              📍 {entrega.edificio?.direccion}, {entrega.edificio?.ciudad}
            </p>
            <div className="flex gap-4 text-xs text-gray-600">
              <span>Items: {entrega.total_items}</span>
              {entrega.peso_total && (
                <span>Peso: {(entrega.peso_total / 1000).toFixed(1)} kg</span>
              )}
            </div>
          </div>

          {/* Verificación de items */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Verificar {tipoLabel}
            </h3>
            {errors.items && (
              <p className="text-xs text-red-600 mb-2">{errors.items}</p>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {entrega.items?.map((item, index) => {
                const isInsumo = item.tipo_item === 'insumo';
                return (
                <div
                  key={item.id || index}
                  className={`p-3 border rounded transition-colors ${
                    itemsVerificados[item.id]
                      ? (isInsumo ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200')
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  {/* Header con nombre y checkbox */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1">
                      {isInsumo 
                        ? <Droplets className="h-4 w-4 text-amber-600 flex-shrink-0" />
                        : <Shirt className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      }
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{item.nombre}</p>
                          {isInsumo && (
                            <Badge className="text-[10px] bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0">
                              Insumo
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-3 text-xs text-gray-600 mt-1">
                          <span>Solicitado: {item.cantidad}</span>
                          {!isInsumo && item.peso && <span>Peso: {item.peso}g</span>}
                        </div>
                      </div>
                    </div>
                    <div 
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer ${
                        itemsVerificados[item.id]
                          ? 'bg-green-500 border-green-500'
                          : 'border-gray-300'
                      }`}
                      onClick={() => handleToggleItem(item.id)}
                    >
                      {itemsVerificados[item.id] && (
                        <CheckCircle className="h-4 w-4 text-white" />
                      )}
                    </div>
                  </div>

                  {/* Controles de cantidad */}
                  {itemsVerificados[item.id] && (
                    <div className={`flex items-center gap-2 mt-2 pt-2 border-t ${isInsumo ? 'border-amber-200' : 'border-green-200'}`}>
                      <span className="text-xs font-medium text-gray-700">Cantidad a entregar:</span>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDecrementCantidad(item.id);
                          }}
                          disabled={isSubmitting || cantidadesEntregadas[item.id] <= 0}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <Input
                          type="number"
                          min="0"
                          value={cantidadesEntregadas[item.id] || 0}
                          onChange={(e) => handleCantidadChange(item.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-7 w-16 text-center text-sm"
                          disabled={isSubmitting}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIncrementCantidad(item.id);
                          }}
                          disabled={isSubmitting}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      {cantidadesEntregadas[item.id] !== item.cantidad && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            cantidadesEntregadas[item.id] < item.cantidad
                              ? 'bg-yellow-50 text-yellow-700 border-yellow-300'
                              : 'bg-blue-50 text-blue-700 border-blue-300'
                          }`}
                        >
                          {cantidadesEntregadas[item.id] < item.cantidad ? 'Entrega parcial' : 'Entrega extra'}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {/* Peso real total (solo lencería vertical) */}
          {requierePesoReal && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-gray-700">
                Peso real total (kg) *
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-blue-600"
                onClick={usarPesoSugerido}
                disabled={isSubmitting}
              >
                Usar sugerido ({calcularPesoEstimadoKg()} kg)
              </Button>
            </div>
            <div className="relative">
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={pesoReal}
                onChange={(e) => {
                  setPesoReal(e.target.value);
                  if (parseFloat(e.target.value) > 0) limpiarError('pesoReal');
                }}
                placeholder="0.00"
                disabled={isSubmitting}
                className={`pr-10 ${errors.pesoReal ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                kg
              </span>
            </div>
            {errors.pesoReal ? (
              <p className="text-xs text-red-600 mt-1">{errors.pesoReal}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Peso estimado por prendas: {calcularPesoEstimadoKg()} kg. Confirme o ajuste con el peso real de la báscula.
              </p>
            )}
          </div>
          )}

          {/* Nombre de quien recibe */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">
              Nombre de quien recibe *
            </label>
            <Input
              value={receptorNombre}
              onChange={(e) => {
                setReceptorNombre(e.target.value);
                if (e.target.value.trim()) limpiarError('receptorNombre');
              }}
              placeholder="Ingrese el nombre completo"
              disabled={isSubmitting}
              className={errors.receptorNombre ? 'border-red-500 focus-visible:ring-red-500' : ''}
            />
            {errors.receptorNombre && (
              <p className="text-xs text-red-600 mt-1">{errors.receptorNombre}</p>
            )}
          </div>

          {/* Observaciones */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">
              Observaciones (opcional)
            </label>
            <Textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Ingrese observaciones adicionales"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          {/* Firma */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-gray-700">
                Firma del receptor *
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearSignature}
                disabled={isSubmitting}
              >
                Limpiar
              </Button>
            </div>
            <div className={`border-2 rounded-lg bg-white ${
              errors.firma ? 'border-red-500' : 'border-gray-300'
            }`}>
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'w-full h-40 cursor-crosshair',
                }}
                backgroundColor="white"
                onEnd={() => limpiarError('firma')}
              />
            </div>
            {errors.firma ? (
              <p className="text-xs text-red-600 mt-1">{errors.firma}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Firme en el recuadro con el dedo o mouse
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 space-y-2">
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Guardando...' : 'Completar Entrega'}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalCompletarEntrega;
